import React, { useState, useMemo } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import { Mail, MapPin, Phone, Github, Linkedin, Twitter } from 'lucide-react';
import { personalInfo } from '../mockData';

// Obfuscate email to prevent spam harvesting
const decodeEmail = (encoded) => {
  return encoded.split('').map(c => String.fromCharCode(c.charCodeAt(0) - 1)).join('');
};

// Encoded version of the email (each char shifted by +1)
const ENCODED_EMAIL = 'lsqbwboufk124Apvumppl/dpn';

const ContactSection = () => {
  const { toast } = useToast();
  const email = useMemo(() => decodeEmail(ENCODED_EMAIL), []);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock form submission
    toast({
      title: "Message Sent!",
      description: "Thank you for reaching out. I'll get back to you soon!",
    });
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  return (
    <section id="contact" className="min-h-screen flex items-center justify-center py-20 relative">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-bold text-white mb-4">
              Get In <span className="text-cyan-400">Touch</span>
            </h2>
            <p className="text-gray-400 text-lg">
              Have a project in mind or want to discuss potential opportunities? Feel free to reach out!
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h3 className="text-3xl font-bold text-cyan-400 mb-6">Contact Information</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-4 p-4 bg-gradient-to-br from-gray-900/50 to-gray-800/30 backdrop-blur-sm border border-gray-700/50 rounded-xl hover:border-cyan-400/50 transition-all duration-300">
                    <Mail className="text-cyan-400 mt-1" size={24} />
                    <div>
                      <p className="text-white font-semibold mb-1">Email</p>
                      <a
                        href={`mailto:${email}`}
                        className="text-gray-400 hover:text-cyan-400 transition-colors"
                      >
                        {email}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-gradient-to-br from-gray-900/50 to-gray-800/30 backdrop-blur-sm border border-gray-700/50 rounded-xl hover:border-cyan-400/50 transition-all duration-300">
                    <MapPin className="text-cyan-400 mt-1" size={24} />
                    <div>
                      <p className="text-white font-semibold mb-1">Location</p>
                      <p className="text-gray-400">{personalInfo.location}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-gradient-to-br from-gray-900/50 to-gray-800/30 backdrop-blur-sm border border-gray-700/50 rounded-xl hover:border-cyan-400/50 transition-all duration-300">
                    <Phone className="text-cyan-400 mt-1" size={24} />
                    <div>
                      <p className="text-white font-semibold mb-1">Phone</p>
                      <p className="text-gray-400">{personalInfo.phone}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div>
                <h3 className="text-2xl font-bold text-white mb-4">Follow Me</h3>
                <div className="flex gap-4">
                  <a
                    href={personalInfo.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 bg-gray-800/50 border border-gray-700/50 rounded-lg hover:border-cyan-400 hover:bg-cyan-400/10 transition-all duration-300"
                  >
                    <Linkedin className="text-cyan-400" size={24} />
                  </a>
                  <a
                    href={personalInfo.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 bg-gray-800/50 border border-gray-700/50 rounded-lg hover:border-cyan-400 hover:bg-cyan-400/10 transition-all duration-300"
                  >
                    <Github className="text-cyan-400" size={24} />
                  </a>
                  <a
                    href="#"
                    className="p-3 bg-gray-800/50 border border-gray-700/50 rounded-lg hover:border-cyan-400 hover:bg-cyan-400/10 transition-all duration-300"
                  >
                    <Twitter className="text-cyan-400" size={24} />
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 backdrop-blur-sm border border-gray-700/50 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-white mb-6">Send Me a Message</h3>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your Name"
                    required
                    className="bg-gray-800/50 border-gray-600 text-white placeholder:text-gray-500 focus:border-cyan-400 transition-colors"
                  />
                </div>

                <div>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Your Email"
                    required
                    className="bg-gray-800/50 border-gray-600 text-white placeholder:text-gray-500 focus:border-cyan-400 transition-colors"
                  />
                </div>

                <div>
                  <Input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="Subject"
                    required
                    className="bg-gray-800/50 border-gray-600 text-white placeholder:text-gray-500 focus:border-cyan-400 transition-colors"
                  />
                </div>

                <div>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Message"
                    rows={5}
                    className="bg-gray-800/50 border-gray-600 text-white placeholder:text-gray-500 focus:border-cyan-400 transition-colors resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-pink-500 to-blue-600 hover:from-pink-600 hover:to-blue-700 text-white font-semibold py-6 rounded-xl transition-all duration-300 hover:scale-[1.02]"
                >
                  Send Message
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
